'use client';

import { c as _c } from "react/compiler-runtime";
import { Check, X } from 'lucide-react';
import { GlassSearchInput } from '@/shared/ui/glass';
import { cn } from '@/lib/utils';
import type { Lens } from './lenses';
import { LENSES } from './lenses';

/**
 * The centre's controls, drawn for the navbar row rather than for the centre's
 * own body.
 *
 *   [ 🔍 Search this history ] [Everything|Needs me|To me|The firm]  4 carried, 63 across 9 days
 *                                                    [Everything seen] [Close]
 *
 * SHAPED FOR THE ROW IT LANDS ON. It is handed to the shell as an element and
 * renders outside the section's width provider, so nothing here may measure the
 * column it speaks for. Every part is a fixed height the register already uses
 * on that row: the field is `SearchAnchor` at 32, the lenses are the glow
 * toggle at 24 inside a 32 shell, and the count is the muted tabular text the
 * anchor's own count is. The row therefore does not change height when the
 * centre opens.
 */

export interface CentreToolbarProps {
  readonly search: string;
  readonly onSearch: (value: string) => void;
  readonly lens: Lens;
  readonly onLens: (lens: Lens) => void;
  readonly carried: number;
  readonly live: number;
  readonly days: number;
}

/** The left slot: the field, the lenses, and what they add up to. */
export function CentreToolbarLeft(t0) {
  const $ = _c(19);
  const {
    search,
    onSearch,
    lens,
    onLens,
    carried,
    live,
    days
  } = t0;
  let t1;
  if ($[0] !== onSearch) {
    t1 = event => onSearch(event.target.value);
    $[0] = onSearch;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== search || $[3] !== t1) {
    t2 = <GlassSearchInput aria-label="Search this history" placeholder="Search this history" value={search} onChange={t1} className="h-8 w-full min-w-0 sm:w-48" />;
    $[2] = search;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== lens || $[6] !== onLens) {
    t3 = LENSES.map(l => <button key={l.value} type="button" onClick={() => onLens(l.value)} className={cn("h-7 whitespace-nowrap rounded-full px-2.5 text-[11px] font-medium transition-all", lens === l.value ? "bg-white dark:bg-white/12 text-[#091E42] dark:text-white shadow-[0_2px_8px_rgba(86,81,255,0.15)]" : "text-[#44546F] dark:text-white/70 hover:text-[#091E42] dark:hover:text-white")}>{l.label}</button>);
    $[5] = lens;
    $[6] = onLens;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t3) {
    t4 = <div className="flex h-8 max-w-full items-center gap-0.5 overflow-x-auto rounded-full bg-white p-0.5 dark:bg-white/12">{t3}</div>;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = carried > 0 ? `${carried} carried, ` : "";
  const t6 = days === 1 ? "day" : "days";
  let t7;
  if ($[10] !== days || $[11] !== live || $[12] !== t5 || $[13] !== t6) {
    t7 = <span className="min-w-0 basis-full text-[11px] font-medium tabular-nums text-slate-400 sm:basis-auto sm:whitespace-nowrap dark:text-white/50">{t5}{live} in this view, {days} {t6} loaded</span>;
    $[10] = days;
    $[11] = live;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t2 || $[16] !== t4 || $[17] !== t7) {
    t8 = <div className="flex min-w-0 flex-wrap items-center gap-2">{t2}{t4}{t7}</div>;
    $[15] = t2;
    $[16] = t4;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
}

/**
 * The right slot: the one thing the centre does to everything at once, and the
 * way back to what the broker was reading.
 *
 * Close is here rather than on the surface itself because the surface has no
 * chrome of its own any more: its controls are this row. Escape does the same,
 * for anyone who never looks up.
 */
export function CentreToolbarRight(t0) {
  const $ = _c(9);
  const {
    onMarkAllRead,
    onClose
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Check className="h-3.5 w-3.5" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== onMarkAllRead) {
    t2 = <button type="button" onClick={onMarkAllRead} className="flex h-8 items-center gap-1.5 rounded-full bg-white dark:bg-white/12 px-3 text-[11px] font-medium text-[#44546F] dark:text-white/70 shadow-[0_2px_6px_rgba(35,29,48,0.05)] transition-shadow hover:text-[#091E42] dark:hover:text-white hover:shadow-[0_2px_8px_rgba(86,81,255,0.15)]">{t1}Everything seen</button>;
    $[1] = onMarkAllRead;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <X className="h-3.5 w-3.5" />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== onClose) {
    t4 = <button type="button" onClick={onClose} aria-label="Close the notification centre" title="Close the centre and go back to what you were reading" className="flex h-8 items-center gap-1.5 rounded-full bg-white dark:bg-white/12 px-3 text-[11px] font-medium text-[#44546F] dark:text-white/70 shadow-[0_2px_6px_rgba(35,29,48,0.05)] transition-shadow hover:text-[#091E42] dark:hover:text-white hover:shadow-[0_2px_8px_rgba(86,81,255,0.15)]">{t3}Close</button>;
    $[4] = onClose;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t2 || $[7] !== t4) {
    t5 = <div className="flex shrink-0 items-center gap-2">{t2}{t4}</div>;
    $[6] = t2;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}